import {
  useStripeAdminActions,
  useStripeCatalog
} from '@lib/stripe/useStripeAdmin'
import { getTenantTypeLabel } from '@lib/tenantTypes'
import {
  Alert,
  Badge,
  Checkbox,
  Grid,
  Group,
  Select,
  Stack,
  Text
} from '@mantine/core'
import { H3, PageContentPaper, Button, openConfirmModal } from '@packages/ui'
import { useSupabase } from '@packages/supabase'
import { useCallback, useEffect, useMemo, useState } from 'react'

type TenantBillingRow = {
  id: string
  name?: string | null
  type?: string | null
  billing_plan?: string | null
  stripe_customer_id?: string | null
  stripe_subscription_id?: string | null
  demo_expiry_date?: string | null
  email?: string | null
  contact_email?: string | null
  tenant_name?: string | null
}

type SubscriptionSnapshot = {
  status?: string | null
  plan?: { name?: string | null } | null
} | null

type Props = {
  tenant: TenantBillingRow
  subscription: SubscriptionSnapshot
  onReload: () => Promise<void> | void
  onCreateCustomer: () => Promise<void>
  onCreateSubscription: (priceId: string) => Promise<void>
  creatingCustomer?: boolean
  creatingSubscription?: boolean
  stripeLoading?: boolean
}

const PER_SEAT_TIERS = ['basic', 'pro', 'elite']
const PACKAGE_TIERS = ['hub', 'nest', 'hive']

export function TenantBillingPanel({
  tenant,
  subscription,
  onReload,
  onCreateCustomer,
  onCreateSubscription,
  creatingCustomer = false,
  creatingSubscription = false,
  stripeLoading = false
}: Props) {
  const { supabase } = useSupabase()
  const {
    setTenantBillingPlan,
    disconnectTenantBilling,
    baselineTenantPlanFeatures
  } = useStripeAdminActions()

  const tenantType =
    tenant.type === 'default' || !tenant.type ? '' : tenant.type
  const { data: catalog = [] } = useStripeCatalog(tenantType, 'eur')

  const billingModel = useMemo(() => {
    if (tenantType === 'hub') return 'package_tenant'
    if (tenantType === 'circle') return 'none'
    return 'per_seat_plans'
  }, [tenantType])

  const planTiers =
    billingModel === 'package_tenant' ? PACKAGE_TIERS : PER_SEAT_TIERS

  const [planTier, setPlanTier] = useState(tenant.billing_plan ?? 'basic')
  const [checkoutPriceId, setCheckoutPriceId] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setPlanTier(tenant.billing_plan ?? planTiers[0] ?? 'basic')
  }, [tenant.billing_plan, planTiers])

  const run = useCallback(
    async (key: string, fn: () => Promise<void>) => {
      setBusy(key)
      setError(null)
      setMessage(null)
      try {
        await fn()
        await onReload()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Operation failed')
      } finally {
        setBusy(null)
      }
    },
    [onReload]
  )

  const fetchSubscription = useCallback(async () => {
    const { data } = await supabase.functions.invoke(
      `stripe/subscription?tenantId=${encodeURIComponent(tenant.id)}`,
      { method: 'GET' }
    )
    return data?.subscription as SubscriptionSnapshot
  }, [supabase, tenant.id])

  if (billingModel === 'none') {
    return (
      <PageContentPaper>
        <Stack gap="sm">
          <H3>Billing</H3>
          <Text size="sm" c="dimmed">
            This ICP ({getTenantTypeLabel(tenantType)}) has no subscription
            billing model.
          </Text>
        </Stack>
      </PageContentPaper>
    )
  }

  const [clearCustomerOnDisconnect, setClearCustomerOnDisconnect] =
    useState(false)

  return (
    <Stack>
      <Stack gap="md">
        <Stack gap={4}>
          <H3>Billing & plan</H3>
          <Text size="sm" c="dimmed">
            Manage subscription linkage and plan tier for{' '}
            {getTenantTypeLabel(tenantType)} tenants. Feature defaults come from
            Plan management; per-tenant overrides are on the Features tab.
          </Text>
        </Stack>

        {error && (
          <Alert color="red" title="Error">
            {error}
          </Alert>
        )}
        {message && (
          <Alert color="green" title="Done">
            {message}
          </Alert>
        )}

        <Grid className="text-sm">
          <Grid.Col span={3}>Billing plan</Grid.Col>
          <Grid.Col span={9}>
            <Badge variant="light" tt="capitalize">
              {tenant.billing_plan ?? 'basic'}
            </Badge>
          </Grid.Col>
          <Grid.Col span={3}>Billing customer</Grid.Col>
          <Grid.Col span={9}>
            <Text ff="monospace" size="sm">
              {tenant.stripe_customer_id ?? '—'}
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>Subscription</Grid.Col>
          <Grid.Col span={9}>
            <Text ff="monospace" size="sm">
              {tenant.stripe_subscription_id ?? '—'}
            </Text>
          </Grid.Col>

          <Grid.Col span={3}>Subscription status</Grid.Col>
          <Grid.Col span={9}>{subscription?.status ?? '—'}</Grid.Col>
          <Grid.Col span={3}>Product</Grid.Col>
          <Grid.Col span={9}>{subscription?.plan?.name ?? '—'}</Grid.Col>

          <Grid.Col span={3}>Plan tier</Grid.Col>
          <Grid.Col span={9}>
            <Group align="flex-end">
              <Select
                data={planTiers.map((t) => ({
                  value: t,
                  label: t.charAt(0).toUpperCase() + t.slice(1)
                }))}
                value={planTier}
                onChange={(v) => v && setPlanTier(v)}
                w={200}
              />
              <Button
                loading={busy === 'set-plan'}
                onClick={() =>
                  run('set-plan', async () => {
                    const result = await setTenantBillingPlan(
                      tenant.id,
                      planTier,
                      {
                        swapStripe: Boolean(tenant.stripe_subscription_id)
                      }
                    )
                    setMessage(
                      result.stripeSwapped
                        ? `Plan set to ${result.billing_plan} (subscription price updated).`
                        : `Plan set to ${result.billing_plan} in database.`
                    )
                  })
                }
              >
                Apply plan
              </Button>
              <Button
                variant="light"
                loading={busy === 'baseline'}
                onClick={() =>
                  openConfirmModal({
                    title: 'Baseline tenant features',
                    children: (
                      <Text size="sm">
                        Clears per-tenant feature overrides and refreshes JWT
                        features from plan <strong>{planTier}</strong>.
                      </Text>
                    ),
                    onConfirm: () =>
                      void run('baseline', async () => {
                        const result = await baselineTenantPlanFeatures(
                          tenant.id,
                          {
                            planTier,
                            clearOverrides: true
                          }
                        )
                        setMessage(
                          `Baseline complete: plan ${result.billing_plan}, ${result.overrides_cleared} override(s) cleared.`
                        )
                      })
                  })
                }
              >
                Baseline from plan
              </Button>
            </Group>
          </Grid.Col>
        </Grid>

        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            Apply plan updates JWT features. With an active subscription, the
            billed price is updated too. Baseline clears tenant feature
            overrides.
          </Text>
        </Stack>

        <Group>
          {!tenant.stripe_customer_id && (
            <Button
              variant={'outline'}
              loading={creatingCustomer || stripeLoading}
              onClick={() => void onCreateCustomer()}
            >
              Create billing customer
            </Button>
          )}
          {tenant.stripe_customer_id && !tenant.stripe_subscription_id && (
            <>
              <Select
                label="Checkout plan"
                data={catalog.map((p) => ({
                  value: p.price_id,
                  label: `${p.name} (${p.tier ?? '—'})`
                }))}
                value={checkoutPriceId}
                onChange={setCheckoutPriceId}
                w={320}
              />
              <Button
                mt={24}
                loading={creatingSubscription || stripeLoading}
                disabled={!checkoutPriceId}
                onClick={() =>
                  checkoutPriceId && void onCreateSubscription(checkoutPriceId)
                }
              >
                Create subscription
              </Button>
            </>
          )}
          {(tenant.stripe_customer_id || tenant.stripe_subscription_id) && (
            <Button
              color="red"
              variant="light"
              loading={busy === 'disconnect'}
              onClick={() =>
                openConfirmModal({
                  title: 'Disconnect billing',
                  children: (
                    <Stack gap="sm">
                      <Text size="sm">
                        Cancels the subscription immediately and clears the
                        subscription link on this tenant.
                      </Text>
                      <Checkbox
                        label="Also clear billing customer ID"
                        checked={clearCustomerOnDisconnect}
                        onChange={(e) =>
                          setClearCustomerOnDisconnect(e.currentTarget.checked)
                        }
                      />
                    </Stack>
                  ),
                  onConfirm: () => {
                    const clearCustomer = clearCustomerOnDisconnect
                    void run('disconnect', async () => {
                      const result = await disconnectTenantBilling(tenant.id, {
                        cancelStripe: true,
                        clearCustomer
                      })
                      setMessage(
                        result.stripeCanceled
                          ? 'Subscription canceled and tenant unlinked.'
                          : 'Tenant billing links cleared.'
                      )
                      await fetchSubscription()
                    })
                  }
                })
              }
            >
              Disconnect billing
            </Button>
          )}
        </Group>
      </Stack>
    </Stack>
  )
}
